// @ts-nocheck
import { forwardRef } from "react";
import Radio, { RadioProps } from "@mui/material/Radio";
import { styled } from "@mui/material/styles";

const colors = {
  primaryBorder: "var(--primary-text)",
  hoverBorder: "var(--gradient-color)",
  hoverBackground: "var(--button-light)",
  disabledBackground: "var(--button-light)",
  disabledInnerDot: "var(--text-light)",
  gradientStart: "var(--gradient-color)",
  gradientEnd: "var(--primary-gradient-color2)",
  gradient: "linear-gradient(100.44deg, var(--gradient-color) 0%, var(--primary-gradient-color2) 110.4%)",
  surfaceWhite: "var(--surface-white)",
};

const transitionDuration = "150ms";
const iconSize = 20;
const paddingSize = 6;
const rootSize = iconSize + paddingSize * 2;
const innerCirleRadius = 4;
const outerCircleRadius = 8;

const getCommonStyles = (circleType: String) => ({
  cx: 10,
  cy: 10,
  transition: `stroke ${transitionDuration} ease-in-out, fill ${transitionDuration} ease-in-out`,
  r: circleType == "inner" ? innerCirleRadius : outerCircleRadius,
  stroke: circleType == "outer" ? colors.primaryBorder : "none",
  fill: circleType == "inner" ? "url(#radio-gradient)" : colors.surfaceWhite,
});

const BaseIconSvg = styled("svg")({
  width: iconSize,
  height: iconSize,
  display: "block",
});

const UncheckedIconSvg = styled(BaseIconSvg)(() => ({
  "& .outerCircle": {
    ...getCommonStyles("outer"),
    strokeWidth: 1.5,
  },
}));

const CheckedIconSvg = styled(BaseIconSvg)(() => ({
  "& .outerCircle": {
    ...getCommonStyles("outer"),
    strokeWidth: 1.5,
  },
  "& .innerCircle": {
    ...getCommonStyles("inner"),
  },
}));

const CustomUncheckedIcon = (
  <UncheckedIconSvg className="unchecked-icon" viewBox="0 0 20 20">
    <circle className="outerCircle" />
  </UncheckedIconSvg>
);

const CustomCheckedIcon = (
  <CheckedIconSvg className="checked-icon" viewBox="0 0 20 20">
    <defs>
      <linearGradient id="radio-gradient" x1="0%" y1="0%" x2="100%" y2="0%" gradientTransform="rotate(100.44)">
        <stop offset="0%" stopColor={colors.gradientStart} />
        <stop offset="110.4%" stopColor={colors.gradientEnd} />
      </linearGradient>
    </defs>
    <circle className="outerCircle" />
    <circle className="innerCircle" />
  </CheckedIconSvg>
);

const StyledRadio = styled(Radio)<RadioProps>(() => ({
  padding: paddingSize,
  margin: 0,
  color: colors.primaryBorder,
  borderRadius: "50%",
  transition: `background-color ${transitionDuration} ease-in-out`,
  width: rootSize,
  height: rootSize,
  boxSizing: "border-box",
  "&.Mui-disabled": {
    pointerEvents: "none",
    cursor: "default",
    backgroundColor: "transparent",
    "& .outerCircle": {
      stroke: "none",
      fill: colors.disabledBackground,
    },
    "&.Mui-checked .checked-icon .innerCircle": {
      fill: colors.disabledInnerDot,
      opacity: 1,
    },
    "& .unchecked-icon .innerHoverCircle": {
      opacity: 0,
    },
  },
}));

const RadioButton = forwardRef<HTMLButtonElement, RadioProps>((props, ref) => {
  return (
    <StyledRadio
      icon={CustomUncheckedIcon}
      checkedIcon={CustomCheckedIcon}
      {...props}
      sx={{
        opacity: `${props.disabled ? "50%" : "100%"}`,
      }}
      ref={ref}
    />
  );
});
RadioButton.displayName = "RadioButton";
export default RadioButton;
